/**
 * A conversa das frentes de trabalho.
 *
 * <p>Os anexos sobem primeiro, em `POST /api/arquivos` — o mesmo caminho das imagens do
 * mundo —, e só depois o comentário é criado com as urls que voltaram. Assim um
 * comentário com cinco arquivos não vira um multipart de 50 MB que falha inteiro quando
 * um arquivo falha, e o upload continua num lugar só no sistema.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { CHAVE_ROADMAP } from './roadmapApi';

export interface AnexoDeComentario {
  id?: number;
  url: string;
  /** Só imagens têm. Sem ela, a tela desenha o ícone da extensão. */
  miniatura?: string | null;
  nomeOriginal: string;
  bytes: number;
}

export interface Comentario {
  id: number;
  autorId: number | null;
  /** Nulo é quem saiu do time — a tela escreve "usuário removido". */
  autorNome: string | null;
  autorHandle: string | null;
  autorCor: string | null;
  /** A url da foto em `/api/arquivos/…`, ou nulo em quem não subiu nenhuma. */
  autorAvatar: string | null;
  texto: string;
  criadoEm: string;
  /** Nulo enquanto ninguém editou. */
  editadoEm: string | null;
  /** Se quem está lendo pode editar e apagar. */
  meu: boolean;
  anexos: AnexoDeComentario[];
}

/**
 * De quem é a conversa: uma frente do roadmap (ou card do Dev Board) ou uma seção do GDD.
 *
 * <p>É o par que decide a rota de leitura e de escrita. Editar e apagar não precisam
 * dele — o id do comentário já basta, e a rota é a mesma nos dois casos.
 */
export type DonoDaConversa = { tipo: 'tarefa' | 'secao'; id: number };

export const chaveComentarios = (dono: DonoDaConversa) =>
  ['comentarios', dono.tipo, dono.id] as const;

/**
 * A rota da conversa, a mesma para ler e para escrever.
 *
 * <p><b>Perdeu o id uma vez, em silêncio.</b> Quando a conversa passou a servir também à
 * seção do GDD, esta função nasceu sem a interpolação — `/api/tarefas//comentarios` —, e
 * só a escrita a usava: a leitura montava a própria rota no `Conversa.tsx`, com o id. A
 * lista abria normal, e todo comentário novo, de frente, card ou seção, levava 400. Agora
 * as duas passam por aqui, e `comentariosApi.test.ts` prende o id na rota.
 */
export const rotaDaConversa = (dono: DonoDaConversa) =>
  dono.tipo === 'tarefa'
    ? `/api/tarefas/${dono.id}/comentarios`
    : `/api/projetos/mistik-island/gdd/secoes/${dono.id}/comentarios`;

/** Sobe um arquivo e devolve o que o servidor guardou dele. */
/**
 * Sobe um anexo e devolve o que a conversa precisa para mostrá-lo.
 *
 * <p><b>Ia para o endpoint errado.</b> Tudo era mandado para {@code /api/arquivos}, que é
 * o serviço das <b>imagens do mundo</b>: ele só aceita PNG, JPEG, WEBP e GIF. Como a dica
 * embaixo da caixa prometia "imagens, PDF ou o que precisar", todo PDF que alguém tentou
 * anexar foi recusado — e, com o erro engolido lá na tela, o arquivo simplesmente sumia.
 *
 * <p>Agora vai para {@code /api/anexos}, que aceita o que a dica promete e devolve
 * imagem para renderizar e o resto para baixar.
 */
export async function subirAnexo(arquivo: File): Promise<AnexoDeComentario> {
  const dados = new FormData();
  dados.append('arquivo', arquivo);
  const salvo = await api<{
    url: string;
    nomeOriginal: string;
    bytes: number;
    imagem: boolean;
  }>('/api/anexos', { method: 'POST', body: dados });

  return {
    url: salvo.url,
    // A miniatura era do serviço de imagens, que gera uma. Aqui não há: a imagem é
    // pequena o bastante para o preview, e gerar miniatura de um zip não quer dizer nada.
    miniatura: salvo.imagem ? salvo.url : null,
    bytes: salvo.bytes,
    nomeOriginal: arquivo.name,
  };
}

export function useMutacoesComentario(dono: DonoDaConversa) {
  const fila = useQueryClient();

  /**
   * Invalida a conversa e o que conta comentários.
   *
   * <p>O roadmap e o quadro entram porque o card mostra quantos comentários a frente
   * tem: sem isto, escrever o primeiro deixaria o card dizendo zero até a próxima
   * visita. Eles são invalidados mesmo quando a conversa é de uma seção do GDD —
   * invalidar de menos deixa número velho na tela, invalidar de mais custa uma consulta
   * que já estava em cache.
   */
  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: chaveComentarios(dono) });
    void fila.invalidateQueries({ queryKey: CHAVE_ROADMAP });
    // O contador 💬 também está no card do Dev Board.
    void fila.invalidateQueries({ queryKey: ['quadro'] });
    // E a bolinha: um comentário pode gerar aviso para quem está escrevendo — ele citou
    // alguém que responde pela frente, ou a frente é dele. Esperar o próximo ciclo de
    // 30s para a bolinha reagir a um clique meu parece que nada aconteceu.
    void fila.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const criar = useMutation({
    mutationFn: (corpo: { texto: string; anexos: AnexoDeComentario[] }) =>
      api<Comentario>(rotaDaConversa(dono), {
        method: 'POST',
        body: JSON.stringify(corpo),
      }),
    onSuccess: recarregar,
  });

  const editar = useMutation({
    mutationFn: ({ id, texto }: { id: number; texto: string }) =>
      api<Comentario>(`/api/comentarios/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ texto }),
      }),
    onSuccess: recarregar,
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api<void>(`/api/comentarios/${id}`, { method: 'DELETE' }),
    onSuccess: recarregar,
  });

  return { criar, editar, excluir };
}
